import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, Clock, Sparkles } from "lucide-react";
import SeoHead from "../components/SeoHead";
import { NewsletterSignup } from "../components/NewsletterSignup";
import { fetchBlogPosts, fetchBlogCategories, blogImageUrl } from "../lib/blogApi";

const FALLBACK_COVER =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' width='600' height='340'>
      <rect width='600' height='340' fill='#f0f4f1'/>
      <text x='50%' y='50%' font-family='sans-serif' font-size='20' fill='#6b7d6f' text-anchor='middle'>BlendSafe</text>
    </svg>`
  );

function BlogCard({ post, featured = false }) {
  const cover = blogImageUrl(post.cover_image_url) || FALLBACK_COVER;
  return (
    <Link
      to={`/blog/${post.slug}`}
      data-testid={`blog-card-${post.slug}`}
      className={`group block overflow-hidden rounded-[20px] border border-surface-line bg-white shadow-card hover:shadow-floating transition-all hover:-translate-y-1 ${
        featured ? "sm:grid sm:grid-cols-2" : ""
      }`}
    >
      <div className={`relative overflow-hidden ${featured ? "aspect-[4/3] sm:aspect-auto sm:h-full" : "aspect-[16/10]"}`}>
        <img
          src={cover}
          alt={post.title}
          loading="lazy"
          onError={(e) => { e.currentTarget.src = FALLBACK_COVER; }}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
        />
        <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#1f3d2e] text-cream-50 text-[11px] font-semibold">
          {post.category_label}
        </span>
      </div>
      <div className={`p-5 ${featured ? "sm:p-8 flex flex-col justify-center" : ""}`}>
        <h3 className={`font-extrabold tracking-[-0.02em] text-ink leading-snug ${featured ? "text-2xl sm:text-3xl" : "text-lg"}`}>
          {post.title}
        </h3>
        <p className="mt-2.5 text-[14px] text-ink-soft/85 leading-relaxed line-clamp-3">
          {post.excerpt}
        </p>
        <div className="mt-4 flex items-center gap-3 text-[12px] text-ink-mute font-medium">
          <span className="inline-flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5" /> {post.reading_minutes} min read
          </span>
          <span className="inline-flex items-center gap-1 text-[#1f3d2e] font-semibold group-hover:gap-2 transition-all">
            Read <ArrowRight className="w-3.5 h-3.5" />
          </span>
        </div>
      </div>
    </Link>
  );
}

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [cats, setCats] = useState([]);
  const [active, setActive] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBlogCategories().then((d) => setCats(d.categories || [])).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = active === "all" ? {} : { category: active };
    fetchBlogPosts(params)
      .then((d) => setPosts(d.posts || []))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, [active]);

  const [featured, ...rest] = posts;
  const totalPublished = useMemo(
    () => cats.reduce((s, c) => s + (c.count || 0), 0),
    [cats]
  );

  return (
    <main className="bg-cream-50 min-h-[80vh]" data-testid="blog-index-page">
      <SeoHead
        title="BlendSafe Blog — Skincare & medication safety guides"
        description="Evidence-informed guides on using skincare with your medication: medication guides, ingredient deep-dives, and routine building."
        canonicalPath="/blog"
      />

      <div className="max-w-screen-xl mx-auto px-5 sm:px-8 py-10 sm:py-14">
        <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] font-semibold text-ink-mute">
          <BookOpen className="w-3.5 h-3.5" /> BlendSafe Blog
        </span>
        <h1
          data-testid="blog-index-headline"
          className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-[-0.03em] text-ink leading-[1.03]"
        >
          Skincare &amp; medication,{" "}
          <span className="font-editorial">made clear.</span>
        </h1>
        <p className="mt-4 text-[15px] sm:text-lg text-ink-soft/85 leading-relaxed max-w-2xl">
          Evidence-informed guides on using skincare with your medication —
          medication guides, ingredient deep-dives, and routine building.
        </p>

        {/* Category filter */}
        <div className="mt-8 flex flex-wrap gap-2" data-testid="blog-category-filter">
          <button
            data-testid="blog-cat-all"
            onClick={() => setActive("all")}
            className={`h-9 px-4 rounded-full text-[13px] font-semibold transition-all ${
              active === "all"
                ? "bg-ink text-cream-50"
                : "bg-white border border-surface-line text-ink-soft hover:border-ink/30"
            }`}
          >
            All ({totalPublished})
          </button>
          {cats.map((c) => (
            <button
              key={c.key}
              data-testid={`blog-cat-${c.key}`}
              onClick={() => setActive(c.key)}
              className={`h-9 px-4 rounded-full text-[13px] font-semibold transition-all ${
                active === c.key
                  ? "bg-ink text-cream-50"
                  : "bg-white border border-surface-line text-ink-soft hover:border-ink/30"
              }`}
            >
              {c.label} ({c.count})
            </button>
          ))}
        </div>

        {/* Posts */}
        {loading ? (
          <div className="mt-12 text-ink-mute text-[14px]" data-testid="blog-loading">
            Loading articles…
          </div>
        ) : posts.length === 0 ? (
          <div
            className="mt-16 flex flex-col items-center text-center py-16 rounded-3xl bg-white border border-surface-line"
            data-testid="blog-empty"
          >
            <Sparkles className="w-8 h-8 text-[#a87a32]" />
            <p className="mt-4 text-lg font-semibold text-ink">Fresh guides are on the way</p>
            <p className="mt-1.5 text-[14px] text-ink-mute max-w-sm">
              New articles publish automatically. Check back soon.
            </p>
            <Link
              to="/check"
              className="mt-6 inline-flex items-center gap-2 h-11 px-6 rounded-full bg-ink text-cream-50 text-[14px] font-bold hover:bg-ink-soft transition-all"
            >
              Check your skincare free <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        ) : (
          <div className="mt-10 space-y-8">
            {featured && <BlogCard post={featured} featured />}
            {rest.length > 0 && (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {rest.map((p) => (
                  <BlogCard key={p.slug} post={p} />
                ))}
              </div>
            )}
          </div>
        )}

        {!loading && posts.length > 0 && (
          <div className="mt-14">
            <NewsletterSignup source="blog-index" variant="panel" />
          </div>
        )}
      </div>
    </main>
  );
}
